// =====================================================
// FIREBASE CLOUD MESSAGING — CLIENT SERVICE (web)
// The browser-side counterpart to the server's pushService. It:
//   • registers the FCM service worker (public/firebase-messaging-sw.js)
//   • requests notification permission
//   • generates the device registration token (getToken + VAPID key)
//   • saves / refreshes that token on the backend (/api/user/fcm-token)
//   • deletes the token on logout
//   • handles FOREGROUND messages (onMessage) by showing a notification so
//     taps deep-link through the SAME service-worker click handler
//
// It reuses the single Firebase `app` created in ../components/firebase — no
// second initializeApp. Every function degrades gracefully: unsupported
// browser, denied permission, or a missing VAPID key are logged and skipped,
// never thrown, so login/logout flows are never blocked by push.
// =====================================================

import { getMessaging, getToken, deleteToken, onMessage, isSupported } from "firebase/messaging";
import { app } from "../components/firebase";
import axios from "axios";

// Same base-URL convention used across the app.
const BASE_URL =
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

// Web Push certificate key pair (public key) from the Firebase console:
// Project Settings → Cloud Messaging → Web configuration → Web Push
// certificates. Supplied via CRA env var so nothing secret is hardcoded.
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Where the FCM service worker is served from (CRA copies /public to web root).
const SW_URL = "/firebase-messaging-sw.js";

let messagingInstance = null; // cached getMessaging(app)
let swRegistration = null; // cached ServiceWorkerRegistration for FCM
let foregroundBound = false; // guard so onMessage is only wired once

// True when this browser can actually do web push (excludes SSR, older iOS
// Safari, private modes without SW, etc.). Cached promise-free check.
async function supported() {
  try {
    return (
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "Notification" in window &&
      (await isSupported())
    );
  } catch {
    return false;
  }
}

// Lazily create the messaging instance from the shared app.
function getMessagingInstance() {
  if (!messagingInstance) messagingInstance = getMessaging(app);
  return messagingInstance;
}

// Register the dedicated FCM service worker (separate file from the app's
// generic /service-worker.js registration in index.js — different scope/file
// so they never collide). Returns the registration or null.
async function ensureServiceWorker() {
  if (swRegistration) return swRegistration;
  try {
    swRegistration = await navigator.serviceWorker.register(SW_URL);
    return swRegistration;
  } catch (err) {
    console.warn("FCM service worker registration failed:", err?.message || err);
    return null;
  }
}

// Auth header from the same localStorage "token" the rest of the app uses.
function authConfig() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

// Persist a freshly generated token on the backend. Safe to call repeatedly —
// the backend uses $addToSet so duplicates are ignored.
async function saveTokenToBackend(token) {
  try {
    await axios.post(`${BASE_URL}/api/user/fcm-token`, { token }, authConfig());
  } catch (err) {
    console.warn("Saving FCM token failed:", err?.response?.data || err?.message);
  }
}

// Wire the FOREGROUND message handler exactly once. The backend sends
// data-only messages, so the notification content lives in payload.data.
// We render it via the SW registration so a click is handled by the SAME
// notificationclick logic used for background notifications (unified nav).
function bindForegroundHandler() {
  if (foregroundBound) return;
  foregroundBound = true;

  const messaging = getMessagingInstance();
  onMessage(messaging, (payload) => {
    try {
      const d = (payload && payload.data) || {};
      const title = d.title || "New notification";
      const options = {
        body: d.body || "",
        icon: d.icon || "/blog.png",
        badge: "/blog.png",
        data: { url: d.url || "/notify", ...d },
        tag: d.type
          ? `${d.type}-${d.chatId || d.blogId || d.announcementId || d.fromUserId || ""}`
          : undefined,
      };

      if (Notification.permission === "granted") {
        const reg = swRegistration;
        if (reg) reg.showNotification(title, options);
        else new Notification(title, options);
      }
    } catch (err) {
      console.warn("Foreground FCM handling failed:", err?.message || err);
    }
  });
}

/**
 * Full registration flow — call after login and on every authenticated app
 * load. Requests permission, generates the token, saves it, and binds the
 * foreground handler. Returns the token string, or null if unavailable.
 */
export async function registerFcmToken() {
  if (!(await supported())) return null;

  if (!VAPID_KEY) {
    console.warn(
      "REACT_APP_FIREBASE_VAPID_KEY is not set — push notifications are disabled until it is configured."
    );
    return null;
  }

  try {
    // Ask for permission (no-op if already granted; resolves to current state
    // if already denied/blocked — we simply stop in that case).
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;

    const registration = await ensureServiceWorker();
    if (!registration) return null;

    const token = await getToken(getMessagingInstance(), {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!token) return null;

    await saveTokenToBackend(token);
    bindForegroundHandler();
    return token;
  } catch (err) {
    console.warn("FCM token registration failed:", err?.message || err);
    return null;
  }
}

/**
 * Logout cleanup — remove this device's token from the backend and delete it
 * locally so a shared browser stops receiving the user's notifications.
 * Must be called BEFORE the session/token is cleared from localStorage.
 */
export async function unregisterFcmToken() {
  if (!(await supported())) return;
  try {
    const messaging = getMessagingInstance();
    // Recover the current token so the backend can drop the exact one.
    let token = null;
    if (VAPID_KEY) {
      const registration = swRegistration || (await ensureServiceWorker());
      if (registration) {
        token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        }).catch(() => null);
      }
    }

    if (token) {
      await axios
        .delete(`${BASE_URL}/api/user/fcm-token`, {
          ...authConfig(),
          data: { token },
        })
        .catch(() => {});
    }

    await deleteToken(messaging).catch(() => {});
  } catch (err) {
    console.warn("FCM token unregister failed:", err?.message || err);
  }
}

/**
 * Bind a listener for navigation requests coming FROM the service worker.
 * When a notification is tapped while the app is running, the SW posts
 * { type: "FCM_NAVIGATE", url }; we hand the url to the provided callback
 * (wired to react-router's navigate in App). Returns an unsubscribe fn.
 */
export function onFcmNavigate(callback) {
  if (!("serviceWorker" in navigator)) return () => {};
  const handler = (event) => {
    const data = event.data || {};
    if (data.type === "FCM_NAVIGATE" && data.url) callback(data.url);
  };
  navigator.serviceWorker.addEventListener("message", handler);
  return () => navigator.serviceWorker.removeEventListener("message", handler);
}
